import { getAPIErrorMessage } from '../../helpers/helpers';
import { PullDataService } from '../../service/PullDataService';
import { PushDataService } from '../../service/PushDataService';
import { NotificationType } from '../../store/storeNotifications';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { arrayDataToText } from '../../util/dataToText';
import { PushDataOption } from '../PushDataNode/PushDataNode';
import { MicrosoftService } from './PullMicrosoftData';

interface UseMicrosoftPushSubmitParams {
  data: any;
  formData: any;
  edges: any;
  setNodeState: any;
  nodeId: string;
  flowId: string;
  setSaving: any;
  setSchema: any;
  setIsLoading: (v: boolean) => void;
  setNotification: (v: any) => void;
  setHasToExecute: (v: boolean) => void;
}

export const useMicrosoftPushSubmit = ({
  data,
  formData,
  edges,
  setNodeState,
  nodeId,
  flowId,
  setSaving,
  setSchema,
  setIsLoading,
  setNotification,
  setHasToExecute,
}: UseMicrosoftPushSubmitParams) => {
  const saveResult = (text: string) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id: nodeId,
      flowId,
      setSaving,
      flowTriggerData: checkExecuteTrigger(data, nodeId),
      changeType: 'execute',
      objectData: { text },
      setSchema,
    });
  };

  const getTextContent = (): string => {
    if (data?.textContentData?.length > 0) {
      return arrayDataToText(data.textContentData);
    }
    return formData.textContent ?? '';
  };

  const onSubmitMicrosoftExcel = async () => {
    const fileId = data?.microsoftFileId;
    const worksheetId = data?.microsoftWorksheetId;

    if (!fileId) {
      return setNotification({
        type: NotificationType.Error,
        message: 'Please select an Excel file!',
      });
    }
    if (!worksheetId) {
      return setNotification({
        type: NotificationType.Error,
        message: 'Please select a worksheet!',
      });
    }

    const text = getTextContent();
    if (!text) {
      return setNotification({
        type: NotificationType.Error,
        message: 'No content to push!',
      });
    }

    let sheetName: string | undefined;
    if (data?.sheetNameData?.length > 0) {
      sheetName = arrayDataToText(data.sheetNameData);
    } else if (formData.name) {
      sheetName = formData.name;
    }

    const isNewSheet = worksheetId === 'new';
    if (isNewSheet && !sheetName?.trim()) {
      return setNotification({
        type: NotificationType.Error,
        message: 'Please enter a name for the new sheet!',
      });
    }

    await PushDataService.pushMicrosoftExcelData({
      fileId,
      worksheetId: isNewSheet ? '' : worksheetId,
      text,
      sheetName,
      createNewSheet: isNewSheet,
    });

    if (!isNewSheet) {
      try {
        const res = await PullDataService.getMicrosoftExcelData({
          fileId,
          worksheetId,
        });
        if (res?.data?.text != null) saveResult(res.data.text);
        else saveResult(text);
      } catch {
        saveResult(text);
      }
    } else {
      saveResult(text);
    }
  };

  const onSubmitMicrosoftWord = async () => {
    const fileId = data?.microsoftWordFileId;
    const text = getTextContent();

    if (!text) {
      return setNotification({
        type: NotificationType.Error,
        message: 'No content to push!',
      });
    }

    if (!fileId) {
      const newDocumentName = (formData.newDocumentName ?? '').trim();
      if (!newDocumentName) {
        return setNotification({
          type: NotificationType.Error,
          message: 'Please enter a name for the new document!',
        });
      }
      await PushDataService.pushMicrosoftWordData({
        text,
        newDocumentName,
      });
      saveResult(text);
      return;
    }

    const pushOption = formData.selectedPushDataOption;
    const specifiedPage =
      pushOption === PushDataOption.NEW_PAGE_AFTER_SPECIFIED_PAGE
        ? formData.specifiedPage
        : undefined;
    if (
      specifiedPage !== undefined &&
      (specifiedPage === '' || isNaN(Number(specifiedPage)) || Number(specifiedPage) < 1)
    ) {
      return setNotification({
        type: NotificationType.Error,
        message: 'Please enter a valid page number (1 or greater).',
      });
    }

    await PushDataService.pushMicrosoftWordData({
      fileId,
      text,
      pushOption,
      specifiedPage:
        specifiedPage !== undefined ? Number(specifiedPage) : undefined,
    });
    try {
      const res = await PullDataService.getMicrosoftWordData({ fileId });
      if (res?.data?.text != null) saveResult(res.data.text);
      else saveResult(text);
    } catch {
      saveResult(text);
    }
  };

  const onSubmitMicrosoftPowerPoint = async () => {
    const fileId = data?.microsoftPowerPointFileId;
    const text = getTextContent();

    if (!text) {
      return setNotification({
        type: NotificationType.Error,
        message: 'No content to push!',
      });
    }

    if (!fileId) {
      const newPresentationName = (formData.newPresentationName ?? '').trim();
      if (!newPresentationName) {
        return setNotification({
          type: NotificationType.Error,
          message: 'Please enter a name for the new presentation!',
        });
      }
      await PushDataService.pushMicrosoftPowerPointData({
        text,
        newPresentationName,
      });
      saveResult(text);
      return;
    }

    const slideOption = formData.selectedSlideOption;

    await PushDataService.pushMicrosoftPowerPointData({
      fileId,
      text,
      slideOption,
    });
    try {
      const res = await PullDataService.getMicrosoftPowerPointData({ fileId });
      if (res?.data?.text != null) saveResult(res.data.text);
      else saveResult(text);
    } catch {
      saveResult(text);
    }
  };

  const onSubmitMicrosoftRawFiles = async () => {
    let fileUrl = '';
    if (data?.fileUrlData?.length > 0) {
      fileUrl = data.fileUrlData.map((item: { text: string }) => item.text).join('');
    } else {
      fileUrl = formData.fileUrl ?? '';
    }

    if (!fileUrl) {
      return setNotification({
        type: NotificationType.Error,
        message: 'Please provide a file URL!',
      });
    }

    let fileName: string | undefined;
    if (data?.fileNameData?.length > 0) {
      fileName = data.fileNameData[0].text;
    } else if (formData.fileName) {
      fileName = formData.fileName;
    }

    const folderId = data?.microsoftRawFilesFolderId || undefined;

    await PushDataService.uploadMicrosoftRawFile({
      fileUrl,
      fileName,
      folderId,
    });
    saveResult(fileUrl);
  };

  const onSubmitMicrosoft = async () => {
    const service = data?.selectedMicrosoftService ?? MicrosoftService.Excel;

    setIsLoading(true);
    try {
      if (service === MicrosoftService.Excel) {
        await onSubmitMicrosoftExcel();
      } else if (service === MicrosoftService.Word) {
        await onSubmitMicrosoftWord();
      } else if (service === MicrosoftService.PowerPoint) {
        await onSubmitMicrosoftPowerPoint();
      } else if (service === MicrosoftService.RawFiles) {
        await onSubmitMicrosoftRawFiles();
      }
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setHasToExecute(true);
      }, 2500);
    }
  };

  return { onSubmitMicrosoft };
};
